import React, { useState } from "react";
import CryptoJS from "crypto-js";
import { FaCopy, FaExchangeAlt } from "react-icons/fa";
import {Helmet} from "react-helmet";

export default function HashGenerator() {
  const [inputText, setInputText] = useState("");
  const [hashAlgorithm, setHashAlgorithm] = useState("MD5");
  const [hashedValue, setHashedValue] = useState("");

  const generateHash = () => {
    let hash;
    switch (hashAlgorithm) {
      case "MD5":
        hash = CryptoJS.MD5(inputText).toString();
        break;
      case "SHA-1":
        hash = CryptoJS.SHA1(inputText).toString();
        break;
      case "SHA-256":
        hash = CryptoJS.SHA256(inputText).toString();
        break;
      default:
        hash = "";
    }
    setHashedValue(hash);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(hashedValue);
  };

  return (

    <>
      

      <Helmet>
        <title>Hash Generator - MD5, SHA-1, SHA-256 | WebUtility.online</title>
        <meta
          name="description"
          content="Generate cryptographic hashes like MD5, SHA-1, and SHA-256 instantly. Use WebUtility's secure, free online hash generator."
        />
        <meta
          name="keywords"
          content="hash generator, md5 hash, sha1 hash, sha256 hash, online hash tool, generate hash, WebUtility"
        />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Online Hash Generator - MD5, SHA-1, SHA-256" />
        <meta
          property="og:description"
          content="Create hashes from text using MD5, SHA-1, or SHA-256. Fast, secure, and free tool by WebUtility.online."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://webutility.online/hash-generator" />
        <meta property="og:image" content="https://webutility.online/assets/og-hash-generator.jpg" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Hash Generator - Free Online MD5 & SHA Hasher" />
        <meta
          name="twitter:description"
          content="Generate MD5, SHA-1, and SHA-256 hashes online for free using WebUtility."
        />
        <meta name="twitter:image" content="https://webutility.online/assets/og-hash-generator.jpg" />
      </Helmet>

      <div className="container mx-auto p-6 max-w-screen-2xl bg-white shadow-lg rounded-xl">
        {/* Heading */}
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800">Hash Generator</h2>
          <p className="text-gray-600">Generate MD5, SHA-1, or SHA-256 hashes instantly.</p>
        </div>

        {/* Input Text */}
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Enter Text:</label>
          <textarea
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
            rows="4"
            placeholder="Enter text to hash..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          ></textarea>
        </div>

        {/* Select Hash Algorithm */}
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Select Algorithm:</label>
          <select
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
            value={hashAlgorithm}
            onChange={(e) => setHashAlgorithm(e.target.value)}
          >
            <option value="MD5">MD5</option>
            <option value="SHA-1">SHA-1</option>
            <option value="SHA-256">SHA-256</option>
          </select>
        </div>

        {/* Generate Button */}
        <button
          onClick={generateHash}
          className="flex items-center justify-center bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition w-full"
        >
          <FaExchangeAlt className="mr-2" /> Generate Hash
        </button>

        {/* Output */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-800">Hashed Output:</h3>
          <div className="bg-gray-100 p-4 rounded-lg flex justify-between items-center">
            <span className="break-all">{hashedValue || "No hash generated yet"}</span>
            {hashedValue && (
              <button
                className="ml-4 text-blue-500 hover:text-blue-700"
                onClick={copyToClipboard}
              >
                <FaCopy />
              </button>
            )}
          </div>
        </div>
        <div className="mt-8">
          <h3 className="text-lg font-medium mb-4">How to Use the Hash Generator</h3>
          <p className="mb-4">
            Follow these steps to generate a cryptographic hash for any text using MD5, SHA-1, or SHA-256:
          </p>

          <h4 className="font-semibold">1. Enter Text</h4>
          <p>
            In the provided text area, input the text you want to generate a hash for. This could be any string or text data.
          </p>

          <h4 className="font-semibold">2. Select Algorithm</h4>
          <p>
            From the dropdown menu, select the hash algorithm you would like to use: MD5, SHA-1, or SHA-256. Each algorithm has different properties, with SHA-256 being more secure than MD5.
          </p>

          <h4 className="font-semibold">3. Generate the Hash</h4>
          <p>
            After entering the text and selecting the desired algorithm, click on the "Generate Hash" button to create the hash.
          </p>

          <h4 className="font-semibold">4. Copy the Hash</h4>
          <p>
            Once the hash is generated, you will see the hashed value below the input area. To copy the hash to your clipboard, click on the copy icon next to the generated hash.
          </p>

          <h4 className="font-semibold">5. Use the Hash</h4>
          <p>
            You can now use the generated hash value for purposes such as password storage, file integrity checks, or digital signatures.
          </p>
        </div>
      </div>
    </>

  );
}
